import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { TracerService } from '../../../services/tracer.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { CanComponentDeactivate } from '../../../guards/unsaved-changes.guard';
import {
  AiModelCatalog,
  AiSetting,
  EffectiveVertexConfig,
  REGION_OPTIONS,
  RegionOption,
  EU_REGIONS,
  AiTestResponse,
} from '../../../models/ai.model';

// Admin-only global AI configuration. Mirrors skills-manager's
// structure (standalone component, client-side admin bounce, backend
// is the real gate). One global setting; Vertex-only engine; any
// region with a non-EU warning. See ai-settings-mockup.html.
@Component({
  selector: 'app-ai-settings',
  imports: [NgClass, FormsModule, DatePipe],
  templateUrl: './ai-settings.html',
})
export class AiSettings implements OnInit, CanComponentDeactivate {
  agRealm: string | null = null;

  loading = false;
  loadError: string | null = null;

  settings: AiSetting | null = null;
  effective: EffectiveVertexConfig | null = null;
  models: AiModelCatalog[] = [];

  // Editable form state
  region = '';
  modelId = '';
  modelVersion = '';
  timeoutSeconds = 60;

  // Snapshot of the last loaded/saved values, used to detect unsaved edits.
  private savedSnapshot = '';

  saving = false;
  saveError: string | null = null;
  saveOk = false;

  testing = false;
  testResult: (AiTestResponse & { region: string; model: string }) | null = null;

  syncing = false;
  syncError: string | null = null;

  regionOptions = REGION_OPTIONS;

  constructor(
    private tracer: TracerService,
    private ls: LocalStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.agRealm = this.ls.getItem('agRealm');
    if (this.agRealm !== 'admins') {
      this.router.navigateByUrl('/');
      return;
    }
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.tracer.getAiSettings().subscribe({
      next: res => {
        this.settings = res.settings;
        this.effective = res.effective;
        // Seed the form from the effective config so the UI always
        // shows what's actually in force (db row, or env/default).
        this.region = res.settings?.region || res.effective.region;
        this.modelId = res.settings?.model_id || res.effective.model;
        this.modelVersion = res.settings?.model_version || res.effective.model_version || '';
        this.timeoutSeconds = res.settings?.timeout_seconds || res.effective.timeout_seconds || 60;
        this.snapshot();
        this.loadModels();
      },
      error: err => {
        this.loading = false;
        this.loadError = err?.error?.error || 'Failed to load AI settings.';
      },
    });
  }

  loadModels(): void {
    // Full catalog (no region filter) — the UI does region filtering
    // client-side so switching region is instant.
    this.tracer.listAiModels().subscribe({
      next: res => {
        this.models = res.models || [];
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.loadError = err?.error?.error || 'Failed to load model catalog.';
      },
    });
  }

  // --- derived ------------------------------------------------------

  get isEuRegion(): boolean {
    return EU_REGIONS.includes(this.region);
  }

  regionsByGroup(group: RegionOption['group']): RegionOption[] {
    return this.regionOptions.filter(r => r.group === group);
  }

  /** Models GA in the currently-selected region. */
  get modelsForRegion(): AiModelCatalog[] {
    if (!this.region) return this.models;
    return this.models.filter(m => m.regions.includes(this.region));
  }

  get publishers(): string[] {
    return [...new Set(this.modelsForRegion.map(m => m.publisher))];
  }

  modelsForPublisher(pub: string): AiModelCatalog[] {
    return this.modelsForRegion.filter(m => m.publisher === pub);
  }

  get selectedModel(): AiModelCatalog | undefined {
    return this.models.find(m => m.model_id === this.modelId);
  }

  /** AI Ticket / AI Evaluation require structured JSON output. */
  get jsonCapable(): boolean {
    return !!this.selectedModel?.supports_json_schema;
  }

  get modelInRegion(): boolean {
    return !!this.selectedModel && this.selectedModel.regions.includes(this.region);
  }

  get canSave(): boolean {
    return !!this.region && !!this.modelId && this.jsonCapable && !this.saving;
  }

  // --- actions ------------------------------------------------------

  onRegionChange(): void {
    this.saveOk = false;
    // If the picked model isn't GA in the new region, clear it so the
    // admin must re-pick from the (now filtered) list.
    if (this.selectedModel && !this.selectedModel.regions.includes(this.region)) {
      this.modelId = '';
      this.modelVersion = '';
    }
  }

  onModelChange(): void {
    this.saveOk = false;
    const m = this.selectedModel;
    if (m && m.versions.length && !m.versions.includes(this.modelVersion)) {
      this.modelVersion = m.versions[0];
    }
  }

  save(): void {
    if (!this.jsonCapable) {
      this.saveError =
        'This model cannot produce structured JSON output — AI Ticket / AI Evaluation require it. Pick a JSON-schema-capable model.';
      return;
    }
    this.saving = true;
    this.saveError = null;
    this.saveOk = false;
    this.tracer
      .putAiSettings({
        engine: 'vertex',
        region: this.region,
        model_id: this.modelId,
        model_version: this.modelVersion,
        timeout_seconds: this.timeoutSeconds,
      })
      .subscribe({
        next: res => {
          this.saving = false;
          this.saveOk = true;
          this.settings = res.settings;
          this.effective = res.effective;
          this.snapshot();
        },
        error: err => {
          this.saving = false;
          this.saveError = err?.error?.error || 'Save failed.';
        },
      });
  }

  test(): void {
    if (!this.region || !this.modelId) return;
    this.testing = true;
    this.testResult = null;
    this.tracer
      .testAiModel({
        region: this.region,
        model_id: this.modelId,
        model_version: this.modelVersion,
      })
      .subscribe({
        next: res => {
          this.testing = false;
          this.testResult = { ...res, region: this.region, model: this.modelId };
        },
        error: err => {
          this.testing = false;
          this.testResult = {
            ok: false,
            error: err?.error?.error || 'Test request failed.',
            latency_ms: 0,
            region: this.region,
            model: this.modelId,
          };
        },
      });
  }

  syncNow(): void {
    this.syncing = true;
    this.syncError = null;
    this.tracer.syncAiModels().subscribe({
      next: () => {
        this.syncing = false;
        this.loadModels();
      },
      error: err => {
        this.syncing = false;
        this.syncError = err?.error?.error || 'Sync failed.';
      },
    });
  }

  get lastSynced(): string | null {
    const stamped = this.models.map(m => m.last_synced_at).filter(Boolean) as string[];
    if (!stamped.length) return null;
    return stamped.sort().reverse()[0];
  }

  // --- unsaved-changes tracking -------------------------------------

  /** Serialise the editable fields; cheap to compare against a snapshot. */
  private currentState(): string {
    return JSON.stringify([this.region, this.modelId, this.modelVersion, this.timeoutSeconds]);
  }

  /** Record the current field values as the clean baseline. */
  private snapshot(): void {
    this.savedSnapshot = this.currentState();
  }

  /** True when the form differs from the last loaded/saved values. */
  hasUnsavedChanges(): boolean {
    return this.currentState() !== this.savedSnapshot;
  }
}
